/**
 * CLN_Break — breakout of the CLN linear-regression bodies, in one file with NO IMPORTS.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE RULE
 *   Every bar carries two levels, built from the LR BODIES of the previous three
 *   CLN bars — never from its own, which is not known until it closes:
 *
 *     buyLevel   = max(cln[i-3 .. i-1].lrBodyHigh) + bufferAtr * ATR
 *     shortLevel = min(cln[i-3 .. i-1].lrBodyLow)  - bufferAtr * ATR
 *
 *   Price breaching a level takes that side and squares off the other, so this is
 *   an always-in-the-market stop-and-reverse system with NO stop of its own.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE GEOMETRY, MEASURED — why the details below are not details
 *
 *   Because lrBodyHigh/lrBodyLow are a REGRESSION body, the channel between the
 *   two levels is NARROWER THAN ONE AVERAGE BAR'S TRUE RANGE: 0.72 x ATR on
 *   crypto, 0.78 x ATR on RWA and equities. Consequences, all measured over
 *   ~650k bars on 2026-09-22:
 *
 *     - at least one level is breached on 95-96% of bars;
 *     - BOTH are breached on 9-18% of bars, so the tie-break below is a design
 *       parameter governing roughly one bar in seven, not an edge case;
 *     - the channel WIDTH is the same at 4H, 8H and 1D, but the flip RATE is not
 *       quite — a faster bar covers more of a fixed-width channel, so it is
 *       ambiguous more often and the pessimistic tie-break reverses on every one
 *       of those. Measured: every 4.3 bars on daily, 3.8 on 8H, 3.7 on 4H. At
 *       0.20% a trade on a 24x7 venue that is ~17%/yr, ~58%/yr and ~119%/yr.
 *
 *   Note buyLevel >= shortLevel always (a max of body highs cannot fall below a
 *   min of body lows over the same bars), so the two levels never cross and a
 *   CLOSE can never be on both sides at once. Only a TOUCH can be ambiguous.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS FILE HAS NO IMPORTS
 *   It is a BYTE-IDENTICAL TWIN, the arrangement dailyBlastEngine.ts and
 *   omSimpleEngine.ts already have:
 *       frontend/src/utils/strategies/clnBreakEngine.ts     <- source of truth
 *       backend/src/services/strategies/clnBreakEngine.ts
 *   guarded by `node scripts/check-engine-parity.mjs`. The chart marks and the
 *   numbers in the study must not merely agree — they must be the same bytes.
 *   Taking the CLN bars and the ATR as ARGUMENTS rather than importing
 *   `computeClnBars` / `atrSeries` (whose paths differ on the two sides) is what
 *   makes that possible.
 *
 *   DO NOT get the bodies from `transformCandles(x, 'CLN')`. That is a
 *   PASSTHROUGH (candleTransforms.ts: `case 'CLN': return candles;`) — it hands
 *   back the RAW candle body and the result looks entirely plausible. The bodies
 *   must come from `computeClnBars`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CAUSALITY — three places it is easy to lose
 *
 *   1. The LEVELS read bars i-3..i-1 only. Bar i's own LR body closes in bar i's
 *      future.
 *   2. The BUFFER and the STOP read `atr[i-1]`, not `atr[i]`. A level is placed
 *      before bar i opens, so it can only be sized by an ATR that has already
 *      printed. Using atr[i] would size today's resting order with today's range.
 *   3. `linreg` fits over n = min(period, i+1), so a body EXISTS from bar 0 but
 *      is fitted on a partial window. Nothing is emitted before `warmup`.
 */

/** The regression length the study MEASURED, and the fallback when no length is
 *  given. The chart adapter passes the chart's own `settings.candle.clnLength`,
 *  so the levels are always built from the bodies on screen (since 2026-09-24;
 *  a pinned 14 on a chart drawn at 50 marked breaks of bodies nobody could see). */
export const CLN_BREAK_DEFAULT_CLN_LENGTH = 14;
/** How many previous LR bodies form the level. Three, as specified. */
export const CLN_BREAK_DEFAULT_LOOKBACK = 3;
/** ATR period the buffer and the stop are measured in. Fixed, not a setting: it
 *  only has to be the same number in every variant for the variants to be
 *  comparable. */
export const CLN_BREAK_ATR_PERIOD = 14;

export type ClnBreakAction = 'BUY' | 'SHORT';
export type ClnBreakPos = 'LONG' | 'SHORT' | 'FLAT';
/** Reason codes — mapped to plain English in signalLabels.ts. */
export type ClnBreakReason =
  | 'CLNB_BREAK_UP'
  | 'CLNB_BREAK_DOWN'
  | 'CLNB_REVERSE_UP'
  | 'CLNB_REVERSE_DOWN'
  | 'CLNB_STOP_LONG'
  | 'CLNB_STOP_SHORT';

/** The only fields of a price bar the rule reads. */
export interface ClnBreakBar {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
}

/** The only fields of a CLN bar the rule reads. `ClnBar` on both sides is a
 *  structural superset of this, so either side's array can be passed directly. */
export interface ClnBreakClnBar {
  time: number;
  lrBodyHigh: number;
  lrBodyLow: number;
  up: boolean;
}

export interface ClnBreakOptions {
  /** Regression length the CLN bars were computed at. Used for the warm-up floor
   *  only — the bodies themselves are already decided by the caller. */
  clnLength?: number;
  /** Number of previous LR bodies the levels span. Default 3. */
  lookback?: number;
  /** Widen each level by this many ATR. 0 = the literal rule.
   *  Measured: 0.5 takes the both-breached rate from 13.5% to 1.2% and cuts
   *  flips by 47% — every ambiguous bar it removes is a reversal that no longer
   *  happens, which is why this dial moves turnover more than any other. */
  bufferAtr?: number;
  /** 'touch' = intrabar high/low against the level (what a resting stop order
   *  does, and the shipped default); 'close' = only a bar closing beyond it.
   *  A close can never be ambiguous — see the header. */
  breach?: 'touch' | 'close';
  /** Take only the side that agrees with the previous bar's LR direction, and
   *  stay flat otherwise. Off in the literal rule. */
  requireLrAgree?: boolean;
  /** Stop in ATR multiples; 0 = none. A stop makes the system NOT always-in, so
   *  it is scored against its own no-stop comparator and never against an
   *  unstopped baseline. */
  stopAtr?: number;
  /** ATR aligned 1:1 with `bars`, nulls before warm-up. Required when
   *  `bufferAtr` or `stopAtr` is non-zero; ignored otherwise. */
  atr?: ReadonlyArray<number | null>;
}

export interface ClnBreakStep {
  time: number;
  /** The levels in force for THIS bar, or null during warm-up. */
  buyLevel: number | null;
  shortLevel: number | null;
  /** Fresh signal on this bar, or null. */
  signal: ClnBreakAction | null;
  reason: ClnBreakReason | null;
  /** Price this bar's signal filled at, or null when there was none. */
  fill: number | null;
  /** Running position AFTER this bar's signal is applied. */
  pos: ClnBreakPos;
  /** True when both levels were breached on this bar — reported so the share of
   *  bars the tie-break decided can be counted rather than assumed. */
  ambiguous: boolean;
  /** True when the bar went flat because the stop was hit (stopAtr > 0 only). */
  stopped: boolean;
}

/**
 * Walk the bars and emit one step per bar.
 *
 * `bars` and `cln` must be ascending, the same length, aligned bar-for-bar, and
 * CLOSED (the chart's forming bar moves, and a level built from a moving body is
 * not a level anyone could have rested an order at).
 */
export function computeClnBreakSteps(
  bars: ReadonlyArray<ClnBreakBar>,
  cln: ReadonlyArray<ClnBreakClnBar>,
  opts: ClnBreakOptions = {},
): ClnBreakStep[] {
  const steps: ClnBreakStep[] = [];
  const n = bars && cln ? Math.min(bars.length, cln.length) : 0;
  if (!n) return steps;

  const rawLen = Number(opts.clnLength);
  const clnLength = Number.isFinite(rawLen) && rawLen >= 2
    ? Math.round(rawLen)
    : CLN_BREAK_DEFAULT_CLN_LENGTH;
  const rawLook = Number(opts.lookback);
  const lookback = Number.isFinite(rawLook) && rawLook >= 1
    ? Math.round(rawLook)
    : CLN_BREAK_DEFAULT_LOOKBACK;
  const rawBuf = Number(opts.bufferAtr);
  const bufferAtr = Number.isFinite(rawBuf) && rawBuf > 0 ? rawBuf : 0;
  const rawStop = Number(opts.stopAtr);
  const stopAtr = Number.isFinite(rawStop) && rawStop > 0 ? rawStop : 0;
  const onClose = opts.breach === 'close';
  const requireAgree = opts.requireLrAgree === true;
  const atr = opts.atr;

  // The warm-up is the SAME in every variant, deliberately: it is the maximum of
  // the regression window, the ATR window and the level window, whether or not
  // this particular variant uses the ATR. Varying it per variant would mean
  // comparing variants over different histories, which measures the history.
  const warmup = Math.max(clnLength, CLN_BREAK_ATR_PERIOD) + lookback;

  let pos: ClnBreakPos = 'FLAT';
  let stopLevel = 0;

  for (let i = 0; i < n; i++) {
    const bar = bars[i];
    let buyLevel: number | null = null;
    let shortLevel: number | null = null;
    let signal: ClnBreakAction | null = null;
    let reason: ClnBreakReason | null = null;
    let fill: number | null = null;
    let ambiguous = false;
    let stopped = false;

    if (i >= warmup) {
      let hi = -Infinity;
      let lo = Infinity;
      let usable = true;
      for (let j = i - lookback; j < i; j++) {
        const b = cln[j];
        if (!b || !isFinite(b.lrBodyHigh) || !isFinite(b.lrBodyLow)) { usable = false; break; }
        if (b.lrBodyHigh > hi) hi = b.lrBodyHigh;
        if (b.lrBodyLow < lo) lo = b.lrBodyLow;
      }

      // The ATR that sizes a level placed BEFORE this bar opens is the previous
      // bar's — see CAUSALITY (2) in the header.
      const prevAtr = atr && i > 0 ? Number(atr[i - 1]) : NaN;
      const needAtr = bufferAtr > 0 || stopAtr > 0;
      if (needAtr && !(isFinite(prevAtr) && prevAtr > 0)) usable = false;

      if (usable) {
        const pad = bufferAtr > 0 ? bufferAtr * prevAtr : 0;
        buyLevel = hi + pad;
        shortLevel = lo - pad;

        // A stop is the adverse event, so it is tested before the levels and wins
        // the bar outright. Allowing a stop AND a re-entry on the same bar would
        // be two fills on one bar, which is not something a resting order does.
        if (stopAtr > 0 && pos !== 'FLAT') {
          if (pos === 'LONG' && bar.low <= stopLevel) {
            fill = Math.min(stopLevel, bar.open);
            reason = 'CLNB_STOP_LONG';
            pos = 'FLAT';
            stopped = true;
          } else if (pos === 'SHORT' && bar.high >= stopLevel) {
            fill = Math.max(stopLevel, bar.open);
            reason = 'CLNB_STOP_SHORT';
            pos = 'FLAT';
            stopped = true;
          }
        }

        if (!stopped) {
          let hitBuy = onClose ? bar.close > buyLevel : bar.high >= buyLevel;
          let hitShort = onClose ? bar.close < shortLevel : bar.low <= shortLevel;

          if (requireAgree) {
            const lrUp = cln[i - 1].up;
            if (!lrUp) hitBuy = false;
            if (lrUp) hitShort = false;
          }

          let take: ClnBreakAction | null = null;
          if (hitBuy && hitShort) {
            ambiguous = true;
            // PESSIMISTIC TIE-BREAK: the side AGAINST the open position is assumed
            // to have filled first. One flip per bar, never two — a bar that
            // crossed both levels could be walked either way and taking both would
            // be choosing the profitable path through it.
            //
            // FLAT is skipped rather than resolved: with no position there is no
            // "adverse" side, and any rule chosen here would be an invention. It
            // can only happen on the first signal after warm-up (or after a stop),
            // so it cannot drive the result.
            if (pos === 'LONG') take = 'SHORT';
            else if (pos === 'SHORT') take = 'BUY';
          } else if (hitBuy && pos !== 'LONG') {
            take = 'BUY';
          } else if (hitShort && pos !== 'SHORT') {
            take = 'SHORT';
          }

          if (take === 'BUY') {
            // WHERE THE FILL HAPPENS, and why it depends on the breach mode.
            //
            // TOUCH: a resting stop order is already in the market, so it fills at
            // the level — except when the bar OPENS through it, which fills at the
            // open and never better. Assuming a fill AT the level on a gap is how
            // a breakout backtest invents money.
            //
            // CLOSE: the signal does not exist until the bar has closed, so the
            // earliest reachable price IS that close. Filling a close-confirmed
            // signal at the level is look-ahead of the most expensive kind: the
            // level sits below the close by construction (that is what confirmed
            // it), so every entry books a profit nobody could take. Measured
            // before this was fixed, it inflated the close variant to +1047% a
            // year on crypto and +103% on equities — absurd on its face, which is
            // the only reason it was caught.
            fill = onClose ? bar.close : Math.max(buyLevel, bar.open);
            signal = 'BUY';
            reason = pos === 'SHORT' ? 'CLNB_REVERSE_UP' : 'CLNB_BREAK_UP';
            pos = 'LONG';
            if (stopAtr > 0) stopLevel = fill - stopAtr * prevAtr;
          } else if (take === 'SHORT') {
            fill = onClose ? bar.close : Math.min(shortLevel, bar.open);
            signal = 'SHORT';
            reason = pos === 'LONG' ? 'CLNB_REVERSE_DOWN' : 'CLNB_BREAK_DOWN';
            pos = 'SHORT';
            if (stopAtr > 0) stopLevel = fill + stopAtr * prevAtr;
          }
        }
      }
    }

    steps.push({
      time: bar.time,
      buyLevel,
      shortLevel,
      signal,
      reason,
      fill,
      pos,
      ambiguous,
      stopped,
    });
  }

  return steps;
}

export interface ClnBreakTrade {
  side: 'LONG' | 'SHORT';
  entryIdx: number;
  entryTime: number;
  entryPrice: number;
  exitIdx: number;
  exitTime: number;
  exitPrice: number;
  /** Signed % move on the side taken, BEFORE costs. */
  grossPct: number;
  /** 'flip' = reversed by the opposite level, 'stop' = the ATR stop, 'open' =
   *  still open at the end of history and marked to the final close. */
  exitReason: 'flip' | 'stop' | 'open';
}

/**
 * Closed trades implied by a step walk. A flip closes the previous side and opens
 * the new one at the SAME price, which is what squaring off into a reversal at one
 * level actually costs.
 *
 * The final open position is returned marked to the last bar's close with
 * `exitReason: 'open'`, so a caller can report the result with and without it
 * rather than silently including a position nobody exited.
 */
export function computeClnBreakTrades(
  bars: ReadonlyArray<ClnBreakBar>,
  steps: ReadonlyArray<ClnBreakStep>,
): ClnBreakTrade[] {
  const out: ClnBreakTrade[] = [];
  const n = Math.min(bars.length, steps.length);
  if (!n) return out;

  let side: 'LONG' | 'SHORT' | null = null;
  let entryIdx = -1;
  let entryPrice = 0;

  const close = (exitIdx: number, exitPrice: number, why: 'flip' | 'stop' | 'open') => {
    if (side === null || entryPrice <= 0) return;
    const move = (exitPrice - entryPrice) / entryPrice;
    out.push({
      side,
      entryIdx,
      entryTime: bars[entryIdx].time,
      entryPrice,
      exitIdx,
      exitTime: bars[exitIdx].time,
      exitPrice,
      grossPct: 100 * (side === 'LONG' ? move : -move),
      exitReason: why,
    });
    side = null;
  };

  for (let i = 0; i < n; i++) {
    const s = steps[i];
    if (s.stopped && s.fill != null) {
      close(i, s.fill, 'stop');
      continue;
    }
    if (!s.signal || s.fill == null) continue;
    close(i, s.fill, 'flip');
    side = s.signal === 'BUY' ? 'LONG' : 'SHORT';
    entryIdx = i;
    entryPrice = s.fill;
  }

  if (side !== null) close(n - 1, bars[n - 1].close, 'open');
  return out;
}
